import { useCallback, useMemo } from "react"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { ChipList } from "@l2r-front/l2r-ui"

import { documentsFiltersConfig } from "../../constants/documentsFiltersConfig"
import { AUTHOR_FILTER, FILE_TYPE_FILTER, FILTERS } from "../../constants/filters"
import { I18N_NAMESPACE } from "../../constants/i18n"
import { useDocumentContext } from "../../contexts/DocumentContext"
import { useDocumentAuthors } from "../../hooks/queries/useDocumentAuthors"
import { useDocumentTypes } from "../../hooks/queries/useDocumentTypes"

export function DocumentFilterList(props) {

    const {
        disableDelete,
        enabledFilters,
        ...otherProps
    } = props

    const [state, dispatch] = useDocumentContext()
    const { filters } = state
    const { applyFilters } = dispatch

    const { data: documentTypes, isLoading: isLoadingDocumentTypes } = useDocumentTypes()
    const { data: users, isLoading: isLoadingUsers } = useDocumentAuthors()
    const isLoading = isLoadingDocumentTypes || isLoadingUsers
    const { t } = useTranslation(I18N_NAMESPACE)

    const displayedFilters = useMemo(() => {
        if (filters && !isLoading) {
            return Object.entries(filters).map(([filterKey, filterValues]) => {
                if (!enabledFilters.includes(filterKey)) {
                    return null
                }
                if (filterKey === AUTHOR_FILTER) {
                    return documentsFiltersConfig[filterKey].getValueList(filterKey, filterValues, users)
                } else if (filterKey === FILE_TYPE_FILTER) {
                    return documentsFiltersConfig[filterKey].getValueList(filterKey, filterValues, documentTypes)
                } else {
                    return documentsFiltersConfig[filterKey].getValueList(filterKey, filterValues, t)
                }
            }).flat().filter(v => !!v.value?.length)
        }
        return null
    }, [documentTypes, enabledFilters, filters, isLoading, t, users])

    const deleteFilter = useCallback((filterIndex) => {
        const filter = displayedFilters[filterIndex]
        const filterKey = filter.key
        const filterValue = [...filters[filterKey]]
        const newFilterValue = documentsFiltersConfig[filterKey].removeValue(filterValue, filter)
        applyFilters({
            ...filters,
            [filterKey]: newFilterValue,
        })
    }, [displayedFilters, filters, applyFilters])

    if (displayedFilters) {
        return <ChipList
            {...otherProps}
            color={disableDelete && "secondary"}
            onDelete={disableDelete ? null : deleteFilter}
            values={displayedFilters}
            variant="filled"
        />
    }
    return null
}

DocumentFilterList.defaultProps = {
    enabledFilters: FILTERS,
}

DocumentFilterList.propTypes = {
    enabledFilters: PropTypes.arrayOf(PropTypes.oneOf(FILTERS)),
}